<template>
  <v-autocomplete
    v-bind="$attrs"
    ref="field"
    v-model="localValue"
    :clearable="clearable && !$attrs.readonly && $attrs.readonly !== ''"
    :items="items"
    :loading="loading"
    no-filter
    :search-input.sync="match"
    class="autocomplete"
    @change="onChange"
    @focus="(e) => e.target.click()"
  >
    <template #selection="data">
      <v-chip
        color="white"
        :selected="data.selected"
        class="chip--select-multi"
        :close="$attrs.multiple"
        @input="removeItem(data.item)"
      >
        <v-avatar>
          <img :src="data.item.photo" />
        </v-avatar>
        {{ data.item.text }}
      </v-chip>
    </template>
    <template #item="data">
      <v-list-tile-avatar>
        <img :src="data.item.photo" />
      </v-list-tile-avatar>
      <v-list-tile-content>
        <v-list-tile-title v-html="data.item.text" />
        <v-list-tile-sub-title
          v-if="data.item.players && data.item.players.length"
        >
          <v-chip
            v-for="player of data.item.players"
            :key="player._id"
            class="my-0"
            color="transparent"
          >
            <v-avatar>
              <v-img :src="player.photo" />
            </v-avatar>
            <v-avatar v-if="player.team">
              <v-img :src="player.team.logo" />
            </v-avatar>
            <v-list-tile-content class="caption">
              <v-list-tile-title>
                <span> {{ player.name }}</span>
              </v-list-tile-title>
              <v-list-tile-sub-title
                v-if="player.team"
                class="caption primary--text text--lighten-1 text-truncate"
              >
                {{ player.team.name }}({{
                  player.team.categoryName.join(' - ')
                }})
              </v-list-tile-sub-title>
            </v-list-tile-content>
          </v-chip>
        </v-list-tile-sub-title>
        <v-list-tile-sub-title
          v-else-if="data.item.teams && data.item.teams.length"
        >
          <v-chip
            v-for="team of data.item.teams"
            :key="team._id"
            class="my-0"
            color="transparent"
          >
            <v-avatar>
              <v-img :src="team.logo" />
            </v-avatar>
            <v-list-tile-content class="caption">
              <v-list-tile-title>
                <span> {{ team.name }}</span>
              </v-list-tile-title>
              <v-list-tile-sub-title
                class="caption primary--text text--lighten-1 text-truncate"
              >
                {{ team.categoryName.join(' - ') }}
              </v-list-tile-sub-title>
            </v-list-tile-content>
          </v-chip>
        </v-list-tile-sub-title>
        <v-list-tile-sub-title
          v-else-if="data.item.entities && data.item.entities.length"
        >
          <v-chip
            v-for="entity of data.item.entities"
            :key="entity._id"
            class="my-0"
            color="transparent"
            small
          >
            <v-avatar>
              <v-img :src="entity.logo" />
            </v-avatar>
            <span class="caption primary--text text--lighten-1">{{
              entity.name
            }}</span>
          </v-chip>
        </v-list-tile-sub-title>
        <v-list-tile-sub-title
          v-else-if="
            matchId &&
            teamId &&
            typeRole.includes('player') &&
            (data.item.position || data.item.convocation || data.item.dorsal)
          "
          class="caption primary--text text--lighten-1"
        >
          <template v-if="data.item.position">
            <span>{{ data.item.position }}</span>
            <span>|</span>
          </template>
          <template v-if="data.item.convocation">
            <span>{{
              $t(`fields.teams.convocations.${data.item.convocation}`)
            }}</span>
            <span>|</span>
          </template>
          <span v-if="data.item.dorsal === 0 || data.item.dorsal">{{
            `${$t('fields.global.dorsal')}: ${data.item.dorsal}`
          }}</span>
        </v-list-tile-sub-title>
      </v-list-tile-content>
    </template>
  </v-autocomplete>
</template>

<script>
import { debounce } from 'debounce'
export default {
  name: 'UserField',
  props: {
    value: {
      type: [String, Array],
      required: false,
      default: '',
    },
    entityId: {
      type: String,
      required: false,
      default: '',
    },
    teamId: {
      type: String,
      required: false,
      default: '',
    },
    matchId: {
      type: String,
      required: false,
      default: '',
    },
    countryId: {
      type: String,
      required: false,
      default: '',
    },
    typeRole: {
      type: [String, Array],
      required: false,
      default: '',
    },
    populateEntity: {
      type: Boolean,
      required: false,
      default: false,
    },
    populateTeam: {
      type: Boolean,
      required: false,
      default: false,
    },
    clearable: {
      type: Boolean,
      required: false,
      default: true,
    },
    hideItems: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  data() {
    return {
      match: '',
      itemList: [],
      cacheList: [],
      loading: false,
      init: false,
    }
  },

  computed: {
    localValue: {
      get() {
        return this.value
      },
      set(value) {
        this.$emit('input', value)
      },
    },
    items() {
      return this.hideItems
        ? this.itemList.filter((item) => !this.localValue.includes(item.value))
        : this.itemList
    },
  },
  watch: {
    value(val, oldVal) {
      if (
        Array.isArray(val) &&
        val.length &&
        (!oldVal || (Array.isArray(oldVal) && !oldVal.length))
      )
        this.init = false
      if (!val.length) this.$emit('changeObjects', [])
      if (this.$attrs.multiple) return
      this.fetch()

      this.nextTick(() => {
        this.$refs.field.lazyValue = val
      })
    },
    match() {
      this.fetch()
    },
    entityId() {
      this.fetch()
    },
    countryId() {
      this.fetch()
    },
    teamId() {
      this.fetch()
    },
    matchId() {
      this.fetch()
    },
    typeRole() {
      this.fetch()
    },
  },
  created() {
    this.fetch = debounce(function () {
      if (this.$refs.field && this.value !== this.$refs.field.lazyValue)
        this.$refs.field.lazyValue = this.value

      let params = { set: false }

      if (this.localValue && !Array.isArray(this.localValue)) {
        params._id = this.localValue
      } else if (
        Array.isArray(this.localValue) &&
        this.localValue.length &&
        !this.init
      ) {
        params._id = this.localValue
      }
      if (this.entityId) params.entity = this.entityId

      if (this.teamId) params.team = this.teamId
      if (this.matchId) params.matchId = this.matchId
      if (this.typeRole) params.typeRole = this.typeRole

      if (this.populateEntity) params.populateEntity = this.populateEntity
      if (this.populateTeam) params.populateTeam = this.populateTeam

      if (this.match) params.match = this.match

      this.loading = true
      this.$store
        .dispatch('users/fetchSelectList', params)
        .then((list) => {
          this.itemList = []
          if (
            this.teamId &&
            this.matchId &&
            this.typeRole.includes('player') &&
            list.some((item) => item.convocation)
          ) {
            // TODO  ----> GROUP BY CONVOCATION
            list.forEach((item) => {
              this.itemList.push(item)
            })
          } else {
            list.forEach((item) => {
              this.itemList.push(item)
            })
          }
          if (
            this.value &&
            !Array.isArray(this.value) &&
            !this.itemList.some((item) => item.value === this.value)
          )
            this.$emit('input', '')

          this.setCache()
          if (this.localValue.length) this.onChange(this.localValue)
        })
        .finally(() => {
          this.loading = false
          this.init = true
        })
    }, 700)
    this.fetch()
  },
  methods: {
    fetch() {},
    setCache() {
      if (Array.isArray(this.localValue)) {
        this.localValue.forEach((v) => {
          const isCached = this.cacheList.some((item) => item.value === v)
          if (!isCached) {
            let item = this.itemList.find((item) => item.value === v)
            if (item) {
              this.cacheList.push(item)
            }
          }
        })
        this.cacheList = this.cacheList.filter((item) =>
          this.localValue.includes(item.value)
        )
        this.cacheList.forEach((cacheItem) => {
          const inList = this.itemList.some(
            (listItem) => listItem.value === cacheItem.value
          )
          if (!inList) this.itemList.push(cacheItem)
        })
      }
    },
    checkValue() {
      if (!Array.isArray(this.value)) {
        if (
          this.value &&
          !Array.isArray(this.value) &&
          !this.itemList.some((item) => item.value === this.value)
        )
          this.$emit('input', '')
      }
    },
    removeItem(item) {
      if (Array.isArray(this.localValue)) {
        const index = this.localValue.indexOf(item.value)
        if (index >= 0) this.localValue.splice(index, 1)
      } else this.localValue = ''
    },
    onChange(value) {
      this.$emit('change', value)
      this.$emit(
        'changeObjects',
        this.itemList.filter((item) => value.includes(item.value))
      )
      if (!this.$attrs.multiple && this.typeRole.includes('player')) {
        let player = value
          ? this.itemList.find((item) => item.value == value)
          : null
        this.$emit('changePlayer', player || {})
      }
    },
  },
}
</script>
<style>
.v-list__tile {
  padding: 8px 16px;
  height: 100%;
}
.v-chip .v-chip__content {
  cursor: pointer;
}
</style>
